/**
 * API Reporting Service Implementation
 * 
 * This service provides the API implementation for reporting operations,
 * communicating with the backend reporting endpoints to manage reports,
 * templates, schedules, and generated artifacts.
 * 
 * @module ApiReportingService
 * @implements {IReportingService}
 * 
 * Service Description:
 * The ApiReportingService handles all reporting-related API calls including:
 * - Report CRUD operations
 * - Report generation and export
 * - Template management
 * - Schedule configuration
 * - Artifact retrieval
 * 
 * @requires ApiClient for HTTP requests
 * @requires IReportingService interface
 */

import { AxiosInstance } from 'axios';
import { Report, ReportTemplate, ReportSchedule } from '../../../types/reporting';
import { IReportingService, ReportGenerationOptions } from '../../interfaces/IReportingService';
import { ServiceResponse, PaginatedResponse } from '../../interfaces/IDataSource';
import { BaseService } from '../base/BaseService';
import ApiClient from '../../../utils/apiClient';

export class ApiReportingService extends BaseService implements IReportingService {
  private api: AxiosInstance;
  private baseURL: string = '/api/v1/reports/';
  private schedulesURL: string = '/api/v1/reports/schedules/';
  private artifactsURL: string = '/api/v1/reports/artifacts/';

  constructor() {
    super('ApiReportingService');
    this.api = ApiClient.getInstance();
    console.log('[ApiReportingService] Initialized with base URL:', this.baseURL);
  }

  // ==================== Report Operations ====================
  
  async getReports(): Promise<PaginatedResponse<Report>> {
    try {
      console.log('[ApiReportingService] Fetching reports...');
      const response = await this.api.get(this.schedulesURL);
      
      // Backend returns schedules, which are essentially report configurations
      const reports = response.data.results.map((schedule: any) => ({
        id: schedule.id,
        name: schedule.name,
        type: schedule.report_type || 'custom',
        description: schedule.description || '',
        parameters: schedule.parameters || {},
        createdAt: schedule.created_at,
        updatedAt: schedule.updated_at,
        status: schedule.is_active ? 'active' : 'inactive',
        // Map schedule data to report format
      }));
      
      return {
        results: reports,
        count: response.data.count || reports.length,
        next: response.data.next || null,
        previous: response.data.previous || null
      };
    } catch (error) {
      this.handleError(error, 'fetching reports');
    }
  }

  async getReport(id: string): Promise<Report> {
    try {
      console.log(`[ApiReportingService] Fetching report ${id}...`);
      const response = await this.api.get(`${this.schedulesURL}${id}/`);
      
      return {
        id: response.data.id,
        name: response.data.name,
        type: response.data.report_type || 'custom',
        description: response.data.description || '',
        parameters: response.data.parameters || {},
        createdAt: response.data.created_at,
        updatedAt: response.data.updated_at,
        status: response.data.is_active ? 'active' : 'inactive',
      };
    } catch (error) {
      this.handleError(error, `fetching report ${id}`);
    }
  }

  async createReport(report: Partial<Report>): Promise<Report> {
    try {
      console.log('[ApiReportingService] Creating report:', report.name);
      
      // Transform to API format
      const apiData = {
        name: report.name,
        report_type: report.type || 'custom',
        description: report.description || '',
        parameters: report.parameters || {},
        schedule_config: {
          frequency: 'once', // Default for non-scheduled reports
        },
        is_active: true
      };
      
      const response = await this.api.post(this.schedulesURL, apiData);
      
      return {
        id: response.data.id,
        name: response.data.name,
        type: response.data.report_type,
        description: response.data.description,
        parameters: response.data.parameters,
        createdAt: response.data.created_at,
        updatedAt: response.data.updated_at,
        status: 'active',
      };
    } catch (error) {
      this.handleError(error, 'creating report');
    }
  }

  async updateReport(id: string, report: Partial<Report>): Promise<Report> {
    try {
      console.log(`[ApiReportingService] Updating report ${id}...`);
      
      // Transform to API format
      const apiData: any = {};
      if (report.name !== undefined) apiData.name = report.name;
      if (report.type !== undefined) apiData.report_type = report.type;
      if (report.description !== undefined) apiData.description = report.description;
      if (report.parameters !== undefined) apiData.parameters = report.parameters;
      
      const response = await this.api.patch(`${this.schedulesURL}${id}/`, apiData);
      
      return {
        id: response.data.id,
        name: response.data.name,
        type: response.data.report_type,
        description: response.data.description,
        parameters: response.data.parameters,
        createdAt: response.data.created_at,
        updatedAt: response.data.updated_at,
        status: response.data.is_active ? 'active' : 'inactive',
      };
    } catch (error) {
      this.handleError(error, `updating report ${id}`);
    }
  }

  async deleteReport(id: string): Promise<void> {
    try {
      console.log(`[ApiReportingService] Deleting report ${id}...`);
      await this.api.delete(`${this.schedulesURL}${id}/`);
      console.log(`[ApiReportingService] Report ${id} deleted successfully`);
    } catch (error) {
      this.handleError(error, `deleting report ${id}`);
    }
  }

  // ==================== Report Generation ====================
  
  async generateReport(id: string, options: ReportGenerationOptions): Promise<Blob> {
    try {
      console.log(`[ApiReportingService] Generating report ${id} with options:`, options);
      
      // Step 1: Trigger report generation
      const generationData = {
        schedule_id: id,
        format: options.format,
        parameters: {
          ...options,
          filters: options.filters || {},
          include_charts: options.includeCharts !== false,
          include_raw_data: options.includeRawData !== false,
          time_range: options.timeRange || 'last_30_days'
        }
      };
      
      // Create execution
      const executionResponse = await this.api.post(
        '/api/v1/reports/executions/', 
        generationData
      );
      
      const executionId = executionResponse.data.id;
      console.log(`[ApiReportingService] Report generation started, execution ID: ${executionId}`);
      
      // Step 2: Poll for completion
      const artifact = await this.pollForCompletion(executionId);
      
      // Step 3: Download the generated file
      const fileResponse = await this.api.get(
        `/api/v1/reports/artifacts/${artifact.id}/download/`,
        {
          responseType: 'blob' // Critical for binary data
        }
      );
      
      console.log(`[ApiReportingService] Report downloaded, size: ${fileResponse.data.size} bytes`);
      return fileResponse.data;
      
    } catch (error) {
      this.handleError(error, 'generating report');
    }
  }

  async previewReport(id: string): Promise<string> {
    try {
      console.log(`[ApiReportingService] Generating preview for report ${id}...`);
      
      // Generate HTML preview
      const options: ReportGenerationOptions = {
        format: 'html',
        includeCharts: true,
        includeRawData: false
      };
      
      const htmlBlob = await this.generateReport(id, options);
      
      // Convert blob to string for preview
      const text = await htmlBlob.text();
      return text;
      
    } catch (error) {
      this.handleError(error, 'previewing report');
    }
  }

  async validateReport(report: Partial<Report>): Promise<ServiceResponse<boolean>> {
    try {
      console.log('[ApiReportingService] Validating report configuration...');
      
      // Client-side validation
      const errors: string[] = [];
      
      if (!report.name || report.name.trim().length === 0) {
        errors.push('Report name is required');
      }
      
      if (!report.type) {
        errors.push('Report type is required');
      }
      
      if (report.parameters) {
        // Validate parameters based on report type
        if (report.type === 'financial' && !report.parameters.account) {
          errors.push('Financial reports require account selection');
        }
        
        if (report.type === 'attendance' && !report.parameters.dateRange) {
          errors.push('Attendance reports require date range');
        }
      }
      
      const isValid = errors.length === 0;
      
      return {
        success: isValid,
        data: isValid,
        error: isValid ? undefined : errors.join('; '),
        meta: {
          validatedAt: new Date().toISOString(),
          errors
        }
      };
      
    } catch (error) {
      console.error('[ApiReportingService] Validation error:', error);
      return {
        success: false,
        data: false,
        error: 'Validation failed due to system error'
      };
    }
  }

  // ==================== Template Operations ====================
  
  async getTemplates(): Promise<PaginatedResponse<ReportTemplate>> {
    try {
      console.log('[ApiReportingService] Fetching report templates...');
      
      // Templates might be schedules with is_template flag or separate endpoint
      // Check backend implementation - for now assume templates are marked schedules
      const response = await this.api.get(this.schedulesURL, {
        params: {
          is_template: true,
          ordering: 'name'
        }
      });
      
      const templates = response.data.results.map((template: any) => this.transformTemplateFromAPI(template));
      
      return {
        results: templates,
        count: response.data.count || templates.length,
        next: response.data.next || null,
        previous: response.data.previous || null
      };
    } catch (error) {
      this.handleError(error, 'fetching templates');
    }
  }

  async getTemplate(id: string): Promise<ReportTemplate> {
    try {
      console.log(`[ApiReportingService] Fetching template ${id}...`);
      const response = await this.api.get(`${this.schedulesURL}${id}/`);
      
      if (!response.data.is_template) {
        throw new Error('Requested resource is not a template');
      }
      
      return this.transformTemplateFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `fetching template ${id}`);
    }
  }

  async createTemplate(template: Partial<ReportTemplate>): Promise<ReportTemplate> {
    try {
      console.log('[ApiReportingService] Creating template:', template.name);
      
      const apiData = {
        name: template.name,
        description: template.description,
        report_type: template.type || 'custom',
        is_template: true,
        is_active: false, // Templates are not scheduled
        parameters: {
          ...template.defaultParameters,
          template_config: {
            placeholders: template.placeholders || [],
            required_fields: template.requiredFields || []
          }
        },
        schedule_config: {
          frequency: 'once' // Templates don't run on schedule
        }
      };
      
      const response = await this.api.post(this.schedulesURL, apiData);
      return this.transformTemplateFromAPI(response.data);
    } catch (error) {
      this.handleError(error, 'creating template');
    }
  }

  async updateTemplate(id: string, template: Partial<ReportTemplate>): Promise<ReportTemplate> {
    try {
      console.log(`[ApiReportingService] Updating template ${id}...`);
      
      const apiData: any = {};
      if (template.name !== undefined) apiData.name = template.name;
      if (template.description !== undefined) apiData.description = template.description;
      if (template.type !== undefined) apiData.report_type = template.type;
      
      if (template.defaultParameters || template.placeholders || template.requiredFields) {
        apiData.parameters = {
          ...template.defaultParameters,
          template_config: {
            placeholders: template.placeholders || [],
            required_fields: template.requiredFields || []
          }
        };
      }
      
      const response = await this.api.patch(`${this.schedulesURL}${id}/`, apiData);
      return this.transformTemplateFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `updating template ${id}`);
    }
  }

  async deleteTemplate(id: string): Promise<void> {
    try {
      console.log(`[ApiReportingService] Deleting template ${id}...`);
      await this.api.delete(`${this.schedulesURL}${id}/`);
      console.log(`[ApiReportingService] Template ${id} deleted successfully`);
    } catch (error) {
      this.handleError(error, `deleting template ${id}`);
    }
  }

  async createReportFromTemplate(templateId: string, data: any): Promise<Report> {
    try {
      console.log(`[ApiReportingService] Creating report from template ${templateId}...`);
      
      // First, get the template
      const template = await this.getTemplate(templateId);
      
      // Merge template defaults with provided data
      const reportData = {
        name: data.name || `${template.name} - ${new Date().toLocaleDateString()}`,
        description: data.description || template.description,
        type: template.type,
        parameters: {
          ...template.defaultParameters,
          ...data.parameters,
          template_id: templateId
        }
      };
      
      // Use existing createReport method
      const report = await this.createReport(reportData);
      
      console.log(`[ApiReportingService] Report created from template:`, report.id);
      return report;
    } catch (error) {
      this.handleError(error, 'creating report from template');
    }
  }

  // ==================== Scheduling Operations ====================
  // Note: These will be fully implemented in Phase 4
  
  async getSchedules(reportId?: string): Promise<ReportSchedule[]> {
    const errorDetails = {
      message: 'Schedule operations not yet implemented',
      component: 'ApiReportingService',
      method: 'getSchedules',
      reportId: reportId,
      timestamp: new Date().toISOString(),
      hint: 'Schedule operations deferred to Phase 4 - backend endpoint needs implementation'
    };
    console.error('[ApiReportingService] Feature not implemented:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  async getSchedule(id: string): Promise<ReportSchedule> {
    // TODO: Implement - deferred to Phase 4
    throw new Error('Schedule operations will be implemented in Phase 4');
  }

  async createSchedule(schedule: Partial<ReportSchedule>): Promise<ReportSchedule> {
    // TODO: Implement - deferred to Phase 4
    throw new Error('Schedule operations will be implemented in Phase 4');
  }

  async updateSchedule(id: string, schedule: Partial<ReportSchedule>): Promise<ReportSchedule> {
    // TODO: Implement - deferred to Phase 4
    throw new Error('Schedule operations will be implemented in Phase 4');
  }

  async deleteSchedule(id: string): Promise<void> {
    // TODO: Implement - deferred to Phase 4
    throw new Error('Schedule operations will be implemented in Phase 4');
  }

  async pauseSchedule(id: string): Promise<void> {
    // TODO: Implement - deferred to Phase 4
    throw new Error('Schedule operations will be implemented in Phase 4');
  }

  async resumeSchedule(id: string): Promise<void> {
    // TODO: Implement - deferred to Phase 4
    throw new Error('Schedule operations will be implemented in Phase 4');
  }

  // ==================== Report History ====================
  
  async getReportHistory(reportId: string): Promise<any[]> {
    try {
      console.log(`[ApiReportingService] Fetching history for report ${reportId}...`);
      
      // Get executions for this schedule/report
      const response = await this.api.get('/api/v1/reports/executions/', {
        params: {
          schedule_id: reportId,
          ordering: '-created_at'
        }
      });
      
      return response.data.results.map((execution: any) => ({
        id: execution.id,
        reportId: execution.schedule_id,
        generatedAt: execution.created_at,
        generatedBy: execution.created_by,
        status: execution.status,
        format: execution.parameters?.format || 'pdf',
        fileSize: execution.artifact?.file_size,
        artifactId: execution.artifact?.id,
        error: execution.error_message
      }));
      
    } catch (error) {
      this.handleError(error, 'fetching report history');
    }
  }

  async getGeneratedReport(historyId: string): Promise<Blob> {
    try {
      console.log(`[ApiReportingService] Downloading historical report ${historyId}...`);
      
      // First get the execution details to find artifact
      const executionResponse = await this.api.get(
        `/api/v1/reports/executions/${historyId}/`
      );
      
      if (!executionResponse.data.artifact?.id) {
        throw new Error('No artifact found for this execution');
      }
      
      // Download the artifact
      const fileResponse = await this.api.get(
        `/api/v1/reports/artifacts/${executionResponse.data.artifact.id}/download/`,
        {
          responseType: 'blob'
        }
      );
      
      return fileResponse.data;
      
    } catch (error) {
      this.handleError(error, 'downloading historical report');
    }
  }

  // ==================== Private Helper Methods ====================
  
  private transformReportFromAPI(data: any): Report {
    return {
      id: data.id,
      name: data.name,
      type: data.report_type || 'custom',
      description: data.description || '',
      parameters: data.parameters || {},
      schedule: data.schedule_config ? {
        id: data.id,
        frequency: data.schedule_config.frequency,
        time: data.schedule_config.time,
      } : undefined,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      lastGenerated: data.last_execution?.created_at,
      status: data.is_active ? 'active' : 'inactive',
    };
  }

  private transformReportForAPI(report: Partial<Report>): any {
    const transformed: any = {};
    
    if (report.name !== undefined) transformed.name = report.name;
    if (report.type !== undefined) transformed.report_type = report.type;
    if (report.description !== undefined) transformed.description = report.description;
    if (report.parameters !== undefined) transformed.parameters = report.parameters;
    if (report.status !== undefined) transformed.is_active = report.status === 'active';
    
    return transformed;
  }

  private transformTemplateFromAPI(data: any): ReportTemplate {
    const templateConfig = data.parameters?.template_config || {};
    
    return {
      id: data.id,
      name: data.name,
      description: data.description || '',
      type: data.report_type || 'custom',
      category: data.category || 'general',
      defaultParameters: {
        ...data.parameters,
        // Remove template-specific config from parameters
        template_config: undefined
      },
      placeholders: templateConfig.placeholders || [],
      requiredFields: templateConfig.required_fields || [],
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      isPublic: data.is_public || false,
      usageCount: data.usage_count || 0
    };
  }

  private transformTemplateForAPI(template: Partial<ReportTemplate>): any {
    const transformed: any = {};
    
    if (template.name !== undefined) transformed.name = template.name;
    if (template.description !== undefined) transformed.description = template.description;
    if (template.type !== undefined) transformed.report_type = template.type;
    if (template.category !== undefined) transformed.category = template.category;
    if (template.isPublic !== undefined) transformed.is_public = template.isPublic;
    
    // Combine parameters and template config
    if (template.defaultParameters || template.placeholders || template.requiredFields) {
      transformed.parameters = {
        ...template.defaultParameters,
        template_config: {
          placeholders: template.placeholders || [],
          required_fields: template.requiredFields || []
        }
      };
    }
    
    // Always mark as template
    transformed.is_template = true;
    transformed.is_active = false;
    
    return transformed;
  }

  private async pollForCompletion(executionId: string, maxAttempts: number = 30): Promise<any> {
    console.log(`[ApiReportingService] Polling for execution ${executionId} completion...`);
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const response = await this.api.get(`/api/v1/reports/executions/${executionId}/`);
      const execution = response.data;
      
      console.log(`[ApiReportingService] Execution status: ${execution.status}`);
      
      if (execution.status === 'completed' && execution.artifact) {
        return execution.artifact;
      }
      
      if (execution.status === 'failed') {
        throw new Error(`Report generation failed: ${execution.error_message || 'Unknown error'}`);
      }
      
      // Wait before next poll (2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    throw new Error('Report generation timed out');
  }

  private handleError(error: any, operation: string): never {
    console.error(`[ApiReportingService] Error during ${operation}:`, error);
    
    // Enhanced error handling
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.data?.message || error.message;
      
      switch (status) {
        case 400:
          throw new Error(`Invalid request: ${message}`);
        case 401:
          throw new Error('Authentication required');
        case 403:
          throw new Error('You do not have permission to perform this action');
        case 404:
          throw new Error('Report not found');
        case 500:
          throw new Error('Server error. Please try again later');
        default:
          throw new Error(message || 'An unexpected error occurred');
      }
    }
    
    // Network or other errors
    throw new Error(error.message || 'Network error. Please check your connection');
  }
}